import { ButtonBase, ButtonBaseProps, SxProps, Theme } from '@mui/material';
import { ReactNode } from 'react';

import {
  getSelectablePanelSelectedSx,
  selectablePanelColumnSx,
  selectablePanelRootSx,
  SelectablePanelOrientation,
  SelectablePanelSelectedAnchor,
} from './selectable-panel-styles';

export type { SelectablePanelOrientation, SelectablePanelSelectedAnchor };

export type SelectablePanelProps = Omit<ButtonBaseProps, 'sx' | 'children'> & {
  /** Whether this panel is the currently selected item. */
  selected?: boolean;
  /** Edge for the inset primary bar when selected. Defaults to `left`. */
  selectedAnchor?: SelectablePanelSelectedAnchor;
  /** Content flow inside the panel. Defaults to `row` (list rows). */
  orientation?: SelectablePanelOrientation;
  children?: ReactNode;
  sx?: SxProps<Theme>;
};

/**
 * Interactive surface for selectable rows in master–detail / sidebar panels.
 * Supports idle, hover, selected, and selected+hover states without media-card layout.
 */
export function SelectablePanel({
  selected = false,
  selectedAnchor = 'left',
  orientation = 'row',
  children,
  sx,
  disabled,
  ...buttonProps
}: SelectablePanelProps) {
  return (
    <ButtonBase
      disableRipple
      disabled={disabled}
      {...buttonProps}
      sx={
        [
          selectablePanelRootSx,
          ...(orientation === 'column' ? [selectablePanelColumnSx] : []),
          ...(selected ? [getSelectablePanelSelectedSx(selectedAnchor)] : []),
          ...(sx !== undefined ? (Array.isArray(sx) ? sx : [sx]) : []),
        ] as SxProps<Theme>
      }
    >
      {children}
    </ButtonBase>
  );
}
